import {
  Component,
  Input,
  DestroyRef,
  ViewChild,
  OnInit,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ThemeService } from 'src/app/core/services/theme.service';
import { HistoricPoint, HistoricStats } from '../../../../models/patch-management.models';
import { BAR_PERCENTAGE, BAR_CATEGORY_PERCENTAGE } from '../../../ticket-analysis/ticket-analysis-chart.constants';

@Component({
  selector: 'app-patch-compliance-trend-view',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './patch-compliance-trend-view.component.html',
  styleUrls: ['./patch-compliance-trend-view.component.scss'],
})
export class PatchComplianceTrendViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) trend: HistoricPoint[] = [];
  @Input({ required: true }) stats!: HistoricStats;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  readonly chartPlugins: Plugin[] = [
    {
      id: 'complianceBarLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;
        ctx.save();
        ctx.font = '700 11px Muli, Arial, Helvetica, sans-serif';
        ctx.fillStyle = '#21b3ff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        this.trend.forEach((point, i) => {
          const x = scales['x'].getPixelForValue(i);
          ctx.fillText(`${point.compliancePct}%`, x, chartArea.top - 4);
        });
        ctx.restore();
      },
    },
  ];

  chartData: ChartConfiguration<'bar' | 'line'>['data'] = { labels: [], datasets: [] };
  chartOptions: ChartConfiguration<'bar' | 'line'>['options'] = {};

  constructor(
    private themeService: ThemeService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.buildChartOptions();
    this.buildChartData();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      this.buildChartData();
      this.chart?.update();
    });
  }

  ngOnChanges(): void {
    this.buildChartData();
    this.chart?.update();
  }

  ngOnDestroy(): void {
    Chart.unregister(...this.chartPlugins);
  }

  absVal(n: number): number {
    return Math.abs(n);
  }

  private formatMonthLabel(month: string): string {
    const [year, monthNum] = month.split('-');
    return new Date(+year!, +monthNum! - 1).toLocaleString('en-US', { month: 'short' });
  }

  private buildChartData(): void {
    const barDefaults = {
      barPercentage: BAR_PERCENTAGE,
      categoryPercentage: BAR_CATEGORY_PERCENTAGE,
      stack: 'compliance',
    };
    this.chartData = {
      labels: this.trend.map((t) => this.formatMonthLabel(t.month)),
      datasets: [
        {
          type: 'bar',
          label: 'Installed',
          data: this.trend.map((t) => t.installedPct),
          backgroundColor: '#00c100',
          borderRadius: 4,
          order: 2,
          ...barDefaults,
        },
        {
          type: 'bar',
          label: 'Missing',
          data: this.trend.map((t) => t.missingPct),
          backgroundColor: '#b9bbbc',
          borderRadius: 4,
          order: 2,
          ...barDefaults,
        },
        {
          type: 'line',
          label: 'Compliance %',
          data: this.trend.map((t) => t.compliancePct),
          borderColor: '#21b3ff',
          backgroundColor: '#21b3ff',
          pointBackgroundColor: '#fff',
          pointBorderColor: '#21b3ff',
          pointBorderWidth: 2,
          pointRadius: 5,
          borderWidth: 3,
          tension: 0.3,
          yAxisID: 'y2',
          order: 1,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    const labelColor = isDark ? '#F1F1F1' : '#555555';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              context.dataset.label === 'Compliance %'
                ? ` Compliance: ${context.parsed.y}%`
                : ` ${context.dataset.label}: ${context.parsed.y}%`,
          },
        },
        patchComplianceGaugeCenterText: false,
      } as any,
      scales: {
        x: { stacked: true, ticks: { color: labelColor }, grid: { display: false } },
        y: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          ticks: { color: labelColor, callback: (v) => v + '%' },
          grid: { color: gridColor },
        },
        y2: { type: 'linear', position: 'right', min: 70, max: 100, display: false, grid: { display: false } },
      },
    };
  }
}
